import {
  formatPartnerDate,
  formatPartnerDateTime,
} from './partnerDateUtils';

export const STAGE_DISPLAY_LABELS = {
  Submitted: 'Submitted',
  Received: 'Received by Helix PAE',
  Approved: 'Approved',
  Active: 'Active',
  'Go-Live Complete': 'Go-Live Complete',
  Completed: 'Completed',
};

export const STAGE_SUBTITLES = {
  Submitted: 'Registered in Helix',
  Received: 'PAE received this',
  Approved: 'PAE reviewed & approved',
  Active: 'Delivery in progress',
  Completed: 'Project closure',
};

export const DEFAULT_STATUS_STAGES = [
  'Submitted',
  'Received',
  'Approved',
  'Active',
  'Go-Live Complete',
  'Completed',
];

const normalizeBoolean = (value) => value === true || value === 'true' || value === 1;

const normalizeStage = (item) => ({
  stage: item?.stage || item?.name || item?.label || '',
  completed: normalizeBoolean(item?.completed),
  completedAt: item?.completedAt || item?.completed_at || null,
});

const getActivityLog = (project) => (
  Array.isArray(project?.activityLog) ? project.activityLog : []
);

const getActivityTimestamp = (project, actions) => {
  const normalizedActions = actions.map((action) => action.toLowerCase());
  const entry = getActivityLog(project).find((item) => (
    normalizedActions.includes(String(item?.action || '').toLowerCase())
  ));

  return entry?.timestamp || entry?.createdAt || entry?.completedAt || null;
};

const activityHasAction = (project, actions) => Boolean(getActivityTimestamp(project, actions));

export const projectHasPaeApproval = (project) => (
  project?.paeApproved === true
  || project?.paeApproved === 'true'
  || activityHasAction(project, ['approved', 'approve'])
);

export const resolveTimelineCurrentStageName = (project) => {
  const status = String(project?.projectStatus || '').trim();

  if (status === 'Completed') {
    return 'Completed';
  }
  if (status === 'Go-Live Complete') {
    return 'Go-Live Complete';
  }
  if (status === 'Active' || status === 'At Risk') {
    return 'Active';
  }
  if (projectHasPaeApproval(project)) {
    return 'Approved';
  }
  if (project?.submittedAt) {
    return 'Received';
  }
  return 'Submitted';
};

const getFallbackCompletedIndex = (project) => {
  const currentIndex = DEFAULT_STATUS_STAGES.indexOf(resolveTimelineCurrentStageName(project));
  if (currentIndex <= 0) {
    return 0;
  }
  return currentIndex - 1;
};

const getStageCompletedAt = (project, stage, index, maxCompletedIndex) => {
  if (index > maxCompletedIndex) {
    return null;
  }

  const fromActivity = getActivityLog(project).find((entry) => {
    const action = String(entry?.action || '').toLowerCase();
    if (stage === 'Submitted') return action === 'submitted' || action === 'submit';
    if (stage === 'Received') return action === 'received';
    if (stage === 'Approved') return action === 'approved' || action === 'approve';
    if (stage === 'Active') return action === 'active';
    if (stage === 'Go-Live Complete') return action.includes('go_live') || action.includes('golive');
    if (stage === 'Completed') return action === 'completed' || action === 'complete';
    return false;
  });

  if (fromActivity) {
    return fromActivity.timestamp || fromActivity.createdAt || fromActivity.completedAt || null;
  }

  if (stage === 'Submitted' || stage === 'Received') {
    return project?.submittedAt || null;
  }

  if (stage === 'Approved') {
    return getActivityTimestamp(project, ['approved', 'approve']) || project?.updatedAt || null;
  }

  return project?.updatedAt || project?.submittedAt || null;
};

export const buildDefaultStatusTimeline = (project) => {
  if (!project) {
    return [];
  }

  const maxCompletedIndex = getFallbackCompletedIndex(project);

  return DEFAULT_STATUS_STAGES.map((stage, index) => {
    const completed = index <= maxCompletedIndex;
    return {
      stage,
      completed,
      completedAt: completed ? getStageCompletedAt(project, stage, index, maxCompletedIndex) : null,
    };
  });
};

export const resolveStatusTimeline = (project) => {
  const rawTimeline = project?.statusTimeline || project?.status_timeline;

  if (Array.isArray(rawTimeline) && rawTimeline.length > 0) {
    return rawTimeline
      .map(normalizeStage)
      .filter((stage) => stage.stage);
  }

  return buildDefaultStatusTimeline(project);
};

export const getTimelineCurrentIndex = (statusTimeline = [], project = null) => {
  if (!statusTimeline.length) {
    return 0;
  }

  if (project) {
    const currentStage = resolveTimelineCurrentStageName(project);
    const stageIndex = statusTimeline.findIndex((stage) => stage.stage === currentStage);
    if (stageIndex !== -1) {
      return stageIndex;
    }
  }

  const firstIncomplete = statusTimeline.findIndex((stage) => !stage.completed);
  if (firstIncomplete === -1) {
    return statusTimeline.length - 1;
  }

  return firstIncomplete;
};

export const formatTimelineDate = (value) => {
  if (!value) {
    return '';
  }

  const formatted = formatPartnerDate(value);
  return formatted === '-' ? '' : formatted;
};

export const formatTimelineDateTime = (value) => {
  if (!value) {
    return '';
  }

  const formatted = formatPartnerDateTime(value);
  return formatted === '-' ? '' : formatted;
};

export const getStageSubtitle = (stageName, project, { isDone, isCurrent }) => {
  if (stageName === 'Go-Live Complete' && !isDone) {
    const targetDate = formatTimelineDate(project?.estimatedGoliveDate);
    return targetDate ? `Target: ${targetDate}` : 'Target date pending';
  }

  if (stageName === 'Active' && isCurrent) {
    return STAGE_SUBTITLES.Active;
  }

  return STAGE_SUBTITLES[stageName] || '';
};

export const getStageExtraLine = (stageName, project, { isCurrent }) => {
  if (stageName !== 'Active' || !isCurrent) {
    return '';
  }

  const goliveDate = formatTimelineDate(project?.estimatedGoliveDate);
  const activeSince = formatTimelineDate(
    getActivityTimestamp(project, ['approved', 'approve', 'active'])
      || project?.estimatedStartDate
      || project?.updatedAt,
  );

  if (!goliveDate && !activeSince) {
    return '';
  }

  if (goliveDate && activeSince) {
    return `Est. go-live ${goliveDate} | Since ${activeSince}`;
  }

  if (goliveDate) {
    return `Est. go-live ${goliveDate}`;
  }

  return activeSince ? `Since ${activeSince}` : '';
};

export const mergeProjectWithTimeline = (previous, updated) => {
  if (!updated) {
    return previous;
  }

  const merged = { ...previous, ...updated };

  if (!Array.isArray(merged.statusTimeline) || merged.statusTimeline.length === 0) {
    merged.statusTimeline = previous?.statusTimeline || merged.statusTimeline;
  }

  return merged;
};
